/* ------------------------------------------------------------------
   frontend/src/api.js
------------------------------------------------------------------ */

const envBase = (import.meta.env.VITE_API_URL?.replace(/\/$/, '')) || '';
const runtimeBase =
  (typeof window !== 'undefined' && (window.localStorage.getItem('api_base_url') || '').trim()) || '';

if (typeof window !== 'undefined' && runtimeBase && !isCompatibleRuntimeBase(runtimeBase)) {
  window.localStorage.removeItem('api_base_url');
}

function isLocalHostname(hostname) {
  return new Set(['localhost', '127.0.0.1']).has(String(hostname || '').trim().toLowerCase());
}

function isCompatibleRuntimeBase(base) {
  if (typeof window === 'undefined') return false;
  const v = String(base || '').trim();
  if (!v) return false;
  try {
    const baseHost = new URL(v).hostname;
    const appHost = window.location.hostname;
    if (baseHost === appHost) return true;
    if (isLocalHostname(baseHost) && isLocalHostname(appHost)) return true;
    return false;
  } catch {
    return false;
  }
}

function shouldUseSameOriginApiFallback() {
  if (typeof window === 'undefined') return false;
  const appHost = String(window.location.hostname || '').trim().toLowerCase();
  if (!appHost) return false;
  if (isLocalHostname(appHost)) return true;
  if (!envBase) return true;
  try {
    const envHost = String(new URL(envBase).hostname || '').trim().toLowerCase();
    if (!envHost) return true;
    if (envHost === appHost) return true;
    if (isLocalHostname(envHost) && isLocalHostname(appHost)) return true;
    return false;
  } catch {
    return true;
  }
}

function guessApiBase() {
  if (typeof window === 'undefined') return '';
  const { protocol, hostname, port } = window.location;
  // Ambiente local padrão deste projeto: frontend :3005 e API :8000
  if (port === '3005') return `${protocol}//${hostname}:8000`;
  return '';
}

function chooseBase() {
  if (isCompatibleRuntimeBase(runtimeBase) && shouldUseSameOriginApiFallback()) {
    return runtimeBase.replace(/\/$/, '');
  }
  if (typeof window !== 'undefined' && envBase) {
    try {
      const envHost = new URL(envBase).hostname;
      if (envHost === window.location.hostname) return envBase;
    } catch {}
  }
  const guessed = guessApiBase();
  if (guessed) return guessed.replace(/\/$/, '');
  return envBase;
}

const BASE  = chooseBase();
const DEBUG = true;

function buildCandidateBases() {
  const out = [];
  const add = (x) => {
    const v = String(x || '').trim().replace(/\/$/, '');
    if (!v) return;
    if (!out.includes(v)) out.push(v);
  };
  const sameOriginFallback = shouldUseSameOriginApiFallback();
  if (isCompatibleRuntimeBase(runtimeBase) && sameOriginFallback) add(runtimeBase);
  add(envBase);
  add(guessApiBase());
  if (typeof window !== 'undefined' && sameOriginFallback) add(window.location.origin);
  add(BASE);
  return out;
}

/* ----------------------- core request ---------------------------- */
async function request(path, options = {}) {
  const token = localStorage.getItem('token') || '';
  const responseType = options.responseType || 'json';
  const expectJson = responseType === 'json' && options.expectJson !== false;
  const isAbsolute = /^https?:\/\//i.test(path);
  const candidateBases = isAbsolute ? [''] : buildCandidateBases();
  const tried = [];
  let lastNetworkErr = null;
  let lastContentTypeErr = '';
  let res;
  let url = isAbsolute ? path : `${(candidateBases[0] || '')}${path}`;

  const headers = {
    Accept: 'application/json',
    ...(options.headers || {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };

  if (isAbsolute) {
    if (DEBUG) console.log('➡️', options.method || 'GET', url, options.body || options);
    try {
      res = await fetch(url, { ...options, headers });
    } catch (err) {
      throw new Error(`Falha de rede: ${err?.message || err}`);
    }
  } else {
    for (const base of candidateBases) {
      url = `${base}${path}`;
      tried.push(url);
      if (DEBUG) console.log('➡️', options.method || 'GET', url, options.body || options);
      try {
        res = await fetch(url, { ...options, headers });
        const ct = (res.headers.get('content-type') || '').toLowerCase();
        const wantsJsonHere = expectJson && res.status !== 204;
        const gotJsonHere = ct.includes('application/json');
        if (wantsJsonHere && !gotJsonHere) {
          // Evita "falso sucesso" quando o host devolve HTML do frontend em rota de API.
          res = undefined;
          lastContentTypeErr = `Resposta inesperada de ${url} (${ct || 'sem content-type'})`;
          continue;
        }
        if (base) localStorage.setItem('api_base_url', base);
        break;
      } catch (err) {
        lastNetworkErr = err;
      }
    }
    if (!res) {
      const msg = lastNetworkErr?.message || lastNetworkErr || lastContentTypeErr || 'erro de rede';
      throw new Error(`Falha de rede: ${msg}. Tentativas: ${tried.join(' | ')}`);
    }
  }

  if (res.status === 401) {
    localStorage.removeItem('token');
    // redireciona para login (rota existe no SPA)
    window.location.href = '/login';
    throw new Error('Sessão expirada. Faça login novamente.');
  }

  if (!res.ok) {
    let msg = `Erro ${res.status}`;
    const ct = res.headers.get('content-type') || '';
    if (ct.includes('application/json')) {
      try {
        const j = await res.json();
        msg = j?.detail || j?.message || msg;
      } catch {}
    } else {
      try {
        const t = await res.text();
        msg = t || msg;
      } catch {}
    }
    throw new Error(msg);
  }

  // 204 No Content
  if (res.status === 204) return null;

  const wantsJson = res.headers.get('content-type')?.includes('application/json');
  if (expectJson && !wantsJson) {
    throw new Error('Resposta inesperada da API (não JSON). Verifique a base da API.');
  }
  if (responseType === 'blob') return res.blob();
  if (responseType === 'text') return res.text();
  return wantsJson ? res.json() : null;
}

export async function openStream(path, options = {}) {
  const token = localStorage.getItem('token') || '';
  const isAbsolute = /^https?:\/\//i.test(path);
  const candidateBases = isAbsolute ? [''] : buildCandidateBases();
  const tried = [];
  const headers = {
    Accept: 'text/event-stream',
    ...(options.headers || {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };

  for (const base of candidateBases) {
    const url = isAbsolute ? path : `${base}${path}`;
    tried.push(url);
    try {
      const res = await fetch(url, {
        method: 'GET',
        cache: 'no-store',
        ...options,
        headers,
      });
      if (res.status === 401) {
        localStorage.removeItem('token');
        window.location.href = '/login';
        throw new Error('Sessão expirada. Faça login novamente.');
      }
      if (!res.ok) continue;
      const ct = (res.headers.get('content-type') || '').toLowerCase();
      if (!ct.includes('text/event-stream')) continue;
      if (base) localStorage.setItem('api_base_url', base);
      return res;
    } catch {
      // tenta próxima base
    }
  }

  throw new Error(`Falha ao abrir stream em tempo real. Tentativas: ${tried.join(' | ')}`);
}

const jsonRequest = (path, method, bodyObj) =>
  request(path, {
    method,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(bodyObj),
  });

const apiCache = new Map();
const apiInflight = new Map();

function cacheKey(path) {
  const token = localStorage.getItem('token') || '';
  return `${token}|${path}`;
}

function clearApiCache(prefix = '') {
  for (const key of Array.from(apiCache.keys())) {
    const [, path = ''] = key.split('|');
    if (!prefix || path.startsWith(prefix)) apiCache.delete(key);
  }
  for (const key of Array.from(apiInflight.keys())) {
    const [, path = ''] = key.split('|');
    if (!prefix || path.startsWith(prefix)) apiInflight.delete(key);
  }
}

function cachedRequest(path, { ttlMs = 60000, force = false } = {}) {
  const key = cacheKey(path);
  const now = Date.now();
  const hit = apiCache.get(key);
  if (!force && hit && hit.expiresAt > now) return Promise.resolve(hit.value);
  if (!force && apiInflight.has(key)) return apiInflight.get(key);
  const p = request(path).then((value) => {
    apiCache.set(key, { value, expiresAt: Date.now() + ttlMs });
    apiInflight.delete(key);
    return value;
  }).catch((err) => {
    apiInflight.delete(key);
    throw err;
  });
  apiInflight.set(key, p);
  return p;
}

const mutateAndClear = async (prefixes, fn) => {
  const out = await fn();
  for (const prefix of prefixes) clearApiCache(prefix);
  return out;
};

/* --------------------------- auth -------------------------------- */
export async function logIn(username, password) {
  const data = await jsonRequest('/api/login', 'POST', { username, password });
  if (!data?.access_token) throw new Error('Token não fornecido.');
  localStorage.setItem('token', data.access_token);
  return data.access_token;
}

export function logOut() {
  localStorage.removeItem('token');
  window.location.href = '/login';
}

/* ------------------------- máquinas ------------------------------ */
export const listMachines = (companyId = null) => {
  const params = new URLSearchParams();
  if (companyId != null && companyId !== "") params.set('company_id', companyId);
  const qs = params.toString();
  return request(`/machines/api/${qs ? `?${qs}` : ''}`);
};
export const listMachinesPaged = (params = {}) => {
  const qs = new URLSearchParams();
  if (params.company_id != null && params.company_id !== "") qs.set('company_id', String(params.company_id));
  if (params.q) qs.set('q', String(params.q).trim());
  if (params.role && params.role !== 'all') qs.set('role', String(params.role));
  if (params.presence && params.presence !== 'all') qs.set('presence', String(params.presence));
  if (params.maintenance && params.maintenance !== 'all') qs.set('maintenance', String(params.maintenance));
  if (params.lifecycle && params.lifecycle !== 'active') qs.set('lifecycle', String(params.lifecycle));
  if (params.sort_by) qs.set('sort_by', String(params.sort_by));
  if (params.sort_direction) qs.set('sort_direction', String(params.sort_direction));
  if (params.page != null) qs.set('page', String(params.page));
  if (params.page_size != null) qs.set('page_size', String(params.page_size));
  if (params.include_inventory) qs.set('include_inventory', 'true');
  const query = qs.toString();
  return request(`/machines/api/paged${query ? `?${query}` : ''}`);
};
export const getMachineDetail = (uuid) =>
  request(`/machines/api/${encodeURIComponent(uuid)}/detail`);
export const listMachineReplacementCandidates = (uuid, q = '') => {
  const params = new URLSearchParams();
  if (String(q || '').trim()) params.set('q', String(q).trim());
  return request(
    `/machines/api/${encodeURIComponent(uuid)}/replacement-candidates${
      params.toString() ? `?${params.toString()}` : ''
    }`
  );
};
export const listMachineStatuses = (companyId = null) => {
  const params = new URLSearchParams();
  if (companyId != null && companyId !== "") params.set('company_id', companyId);
  const qs = params.toString();
  return request(`/machines/api/statuses${qs ? `?${qs}` : ''}`);
};
export const getOnlineMachines = (companyId = null, departmentId = null) => {
  const params = new URLSearchParams();
  if (companyId != null) params.set('company_id', companyId);
  if (departmentId != null) params.set('department_id', departmentId);
  const qs = params.toString();
  return request(`/machines/api/online${qs ? `?${qs}` : ''}`);
};
export const getMachinesOfflineLog = ({ days = 4, companyId = '', includeNeverSeen = true } = {}) => {
  const params = new URLSearchParams();
  params.set('days', String(days || 4));
  params.set('include_never_seen', includeNeverSeen ? 'true' : 'false');
  if (companyId !== '' && companyId != null) params.set('company_id', String(companyId));
  return request(`/machines/api/offline-log?${params.toString()}`);
};
export const getSessions = (uuid) =>
  request(`/machines/api/${encodeURIComponent(uuid)}/sessions`);
export const getMachineUserHistory = (uuid, limit = 3) =>
  request(`/machines/api/${encodeURIComponent(uuid)}/user-history?limit=${encodeURIComponent(limit)}`);
export const listMachineBlockedUsers = (uuid) =>
  request(`/machines/api/${encodeURIComponent(uuid)}/blocked-users`);
export const createMachineBlockedUser = (uuid, body) =>
  jsonRequest(`/machines/api/${encodeURIComponent(uuid)}/blocked-users`, 'POST', body || {});
export const unblockMachineBlockedUser = (uuid, blockId) =>
  jsonRequest(`/machines/api/${encodeURIComponent(uuid)}/blocked-users/${encodeURIComponent(blockId)}/unblock`, 'POST', {});
export const deleteMachine = (uuid, { purge = true, reason } = {}) =>
  request(
    `/machines/api/${encodeURIComponent(uuid)}?purge_commands=${purge ? 'true' : 'false'}`,
    {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ reason }),
    }
  );
export const archiveMachine = (uuid, body) =>
  jsonRequest(`/machines/api/${encodeURIComponent(uuid)}/archive`, 'POST', body || {});
export const restoreMachine = (uuid, body) =>
  jsonRequest(`/machines/api/${encodeURIComponent(uuid)}/restore`, 'POST', body || {});
export const updateMachine = (uuid, body) =>
  jsonRequest(`/machines/api/${encodeURIComponent(uuid)}`, 'PATCH', body);
export const updateMachineDepartmentBulk = (body = {}) =>
  jsonRequest('/machines/api/bulk/department', 'POST', body);
export const configureMachineOnlineAlerts = (body = {}) =>
  jsonRequest('/machines/api/online-alerts', 'POST', body);
export const getNetworkScanConfig = (uuid) =>
  request(`/network-scans/api/machines/${encodeURIComponent(uuid)}/config`);
export const updateNetworkScanConfig = (uuid, body = {}) =>
  jsonRequest(`/network-scans/api/machines/${encodeURIComponent(uuid)}/config`, 'PUT', body);
export const listMachineNetworkScans = (uuid, limit = 50) =>
  request(`/network-scans/api/machines/${encodeURIComponent(uuid)}/scans?limit=${encodeURIComponent(limit)}`);
export const getNetworkScan = (scanId) =>
  request(`/network-scans/api/${encodeURIComponent(scanId)}`);
export const runMachineNetworkScan = (uuid, body = {}) =>
  jsonRequest(`/network-scans/api/machines/${encodeURIComponent(uuid)}/run-manual`, 'POST', body);
export const importNetworkScanDevices = (scanId, body = {}) =>
  jsonRequest(`/network-scans/api/${encodeURIComponent(scanId)}/inventory/import-selected`, 'POST', body);
export const listMachineDiagnostics = (uuid, limit = 50) =>
  request(`/diagnostics/api/machines/${encodeURIComponent(uuid)}/history?limit=${encodeURIComponent(limit)}`);
export const getMachineDiagnostic = (diagnosticId) =>
  request(`/diagnostics/api/${encodeURIComponent(diagnosticId)}`);
export const runMachineDiagnostic = (uuid, body = {}) =>
  jsonRequest(`/diagnostics/api/machines/${encodeURIComponent(uuid)}/run-manual`, 'POST', body);
export const triggerInventory = (uuid) =>
  request(`/machines/api/${encodeURIComponent(uuid)}/inventory_collect`, { method: 'POST' });
export const triggerInventoryBulk = (body = {}) =>
  jsonRequest('/machines/api/bulk/inventory', 'POST', body);
export const triggerRemoteAccessBulk = (body = {}) =>
  jsonRequest('/machines/api/bulk/remote-access', 'POST', body);
export const configureMachineOfflineAlertsBulk = (body = {}) =>
  jsonRequest('/machines/api/bulk/offline-alerts', 'POST', body);
export const triggerClientUpdate = (uuid, body = {}) =>
  jsonRequest(`/machines/api/${encodeURIComponent(uuid)}/client_update`, 'POST', body);
export const dropExecutableToMachine = (uuid, file) => {
  const fd = new FormData();
  fd.append('file', file);
  return request(`/machines/api/${encodeURIComponent(uuid)}/drop-executable`, { method: 'POST', body: fd });
};
export const executeDroppedExecutableOnMachine = (uuid, body = {}) =>
  jsonRequest(`/machines/api/${encodeURIComponent(uuid)}/execute-dropped-executable`, 'POST', body);
export const uploadClientUpdatePackage = (file) => {
  const fd = new FormData();
  fd.append('file', file);
  return request('/machines/api/client_update_package', { method: 'POST', body: fd });
};
export const triggerClientUpdateBulk = (body = {}) =>
  jsonRequest('/machines/api/client_update_bulk', 'POST', body);
export const runMachineDiagnosticBulk = (body = {}) =>
  jsonRequest('/diagnostics/api/machines/run-bulk', 'POST', body);
export const listMachineLocations = (params = {}) => {
  const qs = new URLSearchParams();
  if (params.company_id != null && params.company_id !== '') qs.set('company_id', String(params.company_id));
  if (params.q) qs.set('q', String(params.q).trim());
  if (params.limit != null) qs.set('limit', String(params.limit));
  if (params.max_age_hours != null && params.max_age_hours !== '') qs.set('max_age_hours', String(params.max_age_hours));
  if (params.with_location_only != null) qs.set('with_location_only', params.with_location_only ? 'true' : 'false');
  if (params.online_only != null) qs.set('online_only', params.online_only ? 'true' : 'false');
  const query = qs.toString();
  return request(`/machine-locations/api/latest${query ? `?${query}` : ''}`);
};
export const getMachineLocationHistory = (uuid, params = {}) => {
  const qs = new URLSearchParams();
  if (params.limit != null) qs.set('limit', String(params.limit));
  if (params.before_id != null) qs.set('before_id', String(params.before_id));
  const query = qs.toString();
  return request(`/machine-locations/api/history/${encodeURIComponent(uuid)}${query ? `?${query}` : ''}`);
};
export const dispatchMachineLocationCollection = (body = {}) =>
  jsonRequest('/machine-locations/api/dispatch', 'POST', body || {});

/* --------------------------- ativos ----------------------------- */
export const listAssets = (companyId = null) => {
  const params = new URLSearchParams();
  if (companyId != null && companyId !== '') params.set('company_id', companyId);
  const qs = params.toString();
  return request(`/api/assets${qs ? `?${qs}` : ''}`);
};
export const createAsset = (body) =>
  jsonRequest('/api/assets', 'POST', body || {});
export const updateAsset = (id, body) =>
  jsonRequest(`/api/assets/${encodeURIComponent(id)}`, 'PUT', body || {});
export const listAssetAssignmentHistory = (id) =>
  request(`/api/assets/${encodeURIComponent(id)}/assignment-history`);

/* ------------------------ colaboradores ------------------------ */
export const listCollaborators = (companyId = null, q = '') => {
  const params = new URLSearchParams();
  if (companyId != null && companyId !== '') params.set('company_id', companyId);
  if (q) params.set('q', q);
  const qs = params.toString();
  return request(`/api/collaborators${qs ? `?${qs}` : ''}`);
};
export const createCollaborator = (body) =>
  jsonRequest('/api/collaborators', 'POST', body || {});
export const updateCollaborator = (id, body) =>
  jsonRequest(`/api/collaborators/${encodeURIComponent(id)}`, 'PUT', body || {});

/* ----------------------- preferences ---------------------------- */
export const getPreference = (key) =>
  request(`/api/preferences/${encodeURIComponent(key)}`);
export const setPreference = (key, value) =>
  jsonRequest(`/api/preferences/${encodeURIComponent(key)}`, 'PUT', { value });

/* ------------------------- rustdesk ----------------------------- */
export const getRustDeskConfig = () =>
  request('/api/rustdesk/config');
export const updateRustDeskConfig = (body) =>
  jsonRequest('/api/rustdesk/config', 'PUT', body || {});
export const listRustDeskServerPeers = (params = {}) => {
  const qs = new URLSearchParams();
  if (params.q) qs.set('q', String(params.q).trim());
  if (params.matched != null && params.matched !== '') qs.set('matched', params.matched ? 'true' : 'false');
  if (params.agent_online != null && params.agent_online !== '') qs.set('agent_online', params.agent_online ? 'true' : 'false');
  if (params.limit != null) qs.set('limit', String(params.limit));
  const query = qs.toString();
  return request(`/api/rustdesk/server/peers${query ? `?${query}` : ''}`);
};

/* -------------------------- agents IA --------------------------- */
export const listAgentTools = () =>
  request('/agents/api/tools');
export const listAgentCommandCatalog = (params = {}) => {
  const qs = new URLSearchParams();
  if (params.q) qs.set('q', String(params.q).trim());
  if (params.limit != null) qs.set('limit', String(params.limit));
  const query = qs.toString();
  return request(`/agents/api/command-catalog${query ? `?${query}` : ''}`);
};
export const runAgentAction = (body = {}) =>
  jsonRequest('/agents/api/actions', 'POST', body || {});
export const listAgentApprovals = (params = {}) => {
  const qs = new URLSearchParams();
  if (params.limit != null) qs.set('limit', String(params.limit));
  if (params.status) qs.set('status', String(params.status));
  if (params.tool_name) qs.set('tool_name', String(params.tool_name));
  if (params.company_id != null && params.company_id !== '') qs.set('company_id', String(params.company_id));
  const query = qs.toString();
  return request(`/agents/api/approvals${query ? `?${query}` : ''}`);
};
export const createAgentApproval = (body = {}) =>
  jsonRequest('/agents/api/approvals', 'POST', body || {});
export const approveAgentApproval = (id, body = {}) =>
  jsonRequest(`/agents/api/approvals/${encodeURIComponent(id)}/approve`, 'POST', body || {});
export const rejectAgentApproval = (id, body = {}) =>
  jsonRequest(`/agents/api/approvals/${encodeURIComponent(id)}/reject`, 'POST', body || {});
export const listAgentActionLogs = (params = {}) => {
  const qs = new URLSearchParams();
  if (params.limit != null) qs.set('limit', String(params.limit));
  if (params.tool_name) qs.set('tool_name', String(params.tool_name));
  if (params.status) qs.set('status', String(params.status));
  if (params.company_id != null && params.company_id !== '') qs.set('company_id', String(params.company_id));
  const query = qs.toString();
  return request(`/agents/api/actions/logs${query ? `?${query}` : ''}`);
};

/* ------------------------- templates ----------------------------- */
export const getTemplates = () => cachedRequest('/templates/api/templates', { ttlMs: 60000 });
export const addTemplate = (body) =>
  mutateAndClear(['/templates/api/templates'], () => jsonRequest('/templates/api/templates', 'POST', body));
export const updateTemplate = (id, body) =>
  mutateAndClear(['/templates/api/templates'], () =>
    jsonRequest(`/templates/api/templates/${encodeURIComponent(id)}`, 'PUT', body)
  );
export const deleteTemplate = (id) =>
  mutateAndClear(['/templates/api/templates'], () =>
    request(`/templates/api/templates/${encodeURIComponent(id)}`, { method: 'DELETE' })
  );

/* ------------------------- comandos ------------------------------ */
export const createCommand = (body) =>
  jsonRequest('/commands/api/command', 'POST', body);
export const createBulk = (body) =>
  jsonRequest('/commands/api/bulk', 'POST', body);
export const listBulkLogs = (limit = 50) =>
  request(`/commands/api/bulk/logs?limit=${limit}`);
export const listCommandHistory = ({ limit = 50, companyId = '', machineUuid = '', status = '', q = '' } = {}) => {
  const params = new URLSearchParams();
  params.set('limit', String(limit));
  if (companyId !== '' && companyId != null) params.set('company_id', String(companyId));
  if (machineUuid) params.set('machine_uuid', String(machineUuid).trim());
  if (status) params.set('status', String(status).trim());
  if (q) params.set('q', String(q).trim());
  return request(`/commands/api/history?${params.toString()}`);
};
export const listServerAlertHistory = ({ limit = 50, companyId = '', machineUuid = '', eventType = '' } = {}) => {
  const params = new URLSearchParams();
  params.set('limit', String(limit));
  if (companyId !== '' && companyId != null) params.set('company_id', String(companyId));
  if (machineUuid) params.set('machine_uuid', String(machineUuid).trim());
  if (eventType) params.set('event_type', String(eventType).trim());
  return request(`/machines/api/server-alerts/history?${params.toString()}`);
};
export const cleanupCommands = (body = {}) =>
  jsonRequest('/commands/api/cleanup', 'POST', body);
export const cancelCommand = (commandId, body = {}) =>
  jsonRequest(`/commands/api/command/${encodeURIComponent(commandId)}/cancel`, 'POST', body);
export const getCommandStatus = (commandId) =>
  request(`/commands/api/command/${encodeURIComponent(commandId)}`);
export const listTicketCommands = (ticketId, limit = 10) =>
  request(`/commands/api/tickets/${encodeURIComponent(ticketId)}?limit=${encodeURIComponent(limit)}`);

/* -------------------- active directory -------------------------- */
export const runDomainAction = (machineUuid, action, payload = {}, options = {}) => {
  const body = {
    action,
    payload: payload || {},
    auto_finalize: options.autoFinalize !== false,
  };
  if (options.temporaryCredential) {
    body.temporary_credential = {
      username: String(options.temporaryCredential.username || '').trim(),
      password: String(options.temporaryCredential.password || ''),
    };
  }
  return jsonRequest(`/machines/api/ad/${encodeURIComponent(machineUuid)}/action`, 'POST', body);
};

/* ------------------------- scripts (.ps1) ----------------------- */
export const listScriptPacks = () =>
  request('/scripts/api/packs');
export const uploadScriptPack = (name, files = []) => {
  const fd = new FormData();
  fd.append('name', String(name || '').trim());
  for (const file of files || []) fd.append('files', file);
  return request('/scripts/api/packs', { method: 'POST', body: fd });
};
export const deleteScriptPack = (packId) =>
  request(`/scripts/api/packs/${encodeURIComponent(packId)}`, { method: 'DELETE' });
export const sendScriptPack = (body) =>
  jsonRequest('/scripts/api/send', 'POST', body);
export const sendScriptPackBulk = (body) =>
  jsonRequest('/scripts/api/send-bulk', 'POST', body);

/* -------------------- catálogo de aplicativos ------------------- */
export const listApplicationPackages = (includeInactive = true) =>
  request(`/applications/api/packages?include_inactive=${includeInactive ? 'true' : 'false'}`);
export const createApplicationPackage = (formData) =>
  request('/applications/api/packages', { method: 'POST', body: formData });
export const createApplicationPackageVersion = (packageId, formData) =>
  request(`/applications/api/packages/${encodeURIComponent(packageId)}/versions`, {
    method: 'POST', body: formData,
  });
export const updateApplicationPackage = (packageId, body = {}) =>
  jsonRequest(`/applications/api/packages/${encodeURIComponent(packageId)}`, 'PATCH', body);
export const deactivateApplicationPackage = (packageId) =>
  request(`/applications/api/packages/${encodeURIComponent(packageId)}`, { method: 'DELETE' });
export const installApplicationPackage = (packageId, body = {}) =>
  jsonRequest(`/applications/api/packages/${encodeURIComponent(packageId)}/install`, 'POST', body);
export const listApplicationDeployments = (params = {}) => {
  const qs = new URLSearchParams();
  if (params.package_id != null && params.package_id !== '') qs.set('package_id', String(params.package_id));
  qs.set('limit', String(params.limit || 100));
  return request(`/applications/api/deployments?${qs.toString()}`);
};

/* ------------------------- companhias ---------------------------- */
export const listCompanies = () => cachedRequest('/api/companies', { ttlMs: 120000 });
export const createCompany = (body) =>
  mutateAndClear(['/api/companies', '/api/departments', '/api/printers', '/branding/api'], () =>
    jsonRequest('/api/companies', 'POST', body || {})
  );
export const updateCompany = (id, body) =>
  mutateAndClear(['/api/companies', '/api/departments', '/api/printers', '/branding/api'], () =>
    jsonRequest(`/api/companies/${encodeURIComponent(id)}`, 'PUT', body || {})
  );
export const deleteCompany = (id) =>
  mutateAndClear(['/api/companies', '/api/departments', '/api/printers', '/branding/api'], () =>
    request(`/api/companies/${encodeURIComponent(id)}`, { method: 'DELETE' })
  );

/* --------------------------- vpn -------------------------------- */
export const listVpnProfiles = (companyId = null) =>
  request(`/api/vpn/profiles${companyId ? `?company_id=${companyId}` : ''}`);
export const createVpnProfile = (body) =>
  jsonRequest('/api/vpn/profiles', 'POST', body || {});
export const updateVpnProfile = (id, body) =>
  jsonRequest(`/api/vpn/profiles/${encodeURIComponent(id)}`, 'PUT', body || {});
export const deleteVpnProfile = (id) =>
  request(`/api/vpn/profiles/${encodeURIComponent(id)}`, { method: 'DELETE' });
export const listVpnPeers = (profileId) =>
  request(`/api/vpn/profiles/${encodeURIComponent(profileId)}/peers`);
export const deployVpnProfile = (profileId, body = {}) =>
  jsonRequest(`/api/vpn/profiles/${encodeURIComponent(profileId)}/deploy`, 'POST', body || {});
export const listVpnDirectCandidates = (profileId) =>
  request(`/api/vpn/profiles/${encodeURIComponent(profileId)}/direct-candidates`);
export const listVpnDirectLinks = (profileId) =>
  request(`/api/vpn/profiles/${encodeURIComponent(profileId)}/direct-links`);
export const refreshVpnDirectRuntime = (profileId) =>
  jsonRequest(`/api/vpn/profiles/${encodeURIComponent(profileId)}/direct-runtime-refresh`, 'POST', {});
export const createVpnDirectLink = (profileId, body = {}) =>
  jsonRequest(`/api/vpn/profiles/${encodeURIComponent(profileId)}/direct-links`, 'POST', body || {});
export const deleteVpnDirectLink = (profileId, ruleId) =>
  request(
    `/api/vpn/profiles/${encodeURIComponent(profileId)}/direct-links/${encodeURIComponent(ruleId)}`,
    { method: 'DELETE' }
  );
export const updateMachineVpnDirectAccess = (machineUuid, body = {}) =>
  jsonRequest(`/api/vpn/machines/${encodeURIComponent(machineUuid)}/direct-access`, 'PUT', body || {});
export const getVpnGatewayStatus = () =>
  request('/api/vpn/gateway');
export const listVpnGatewayFlows = () =>
  request('/api/vpn/gateway/flows');
export const syncVpnGateway = (body = {}) =>
  jsonRequest('/api/vpn/gateway/sync', 'POST', body || {});

/* ----------------------- departamentos -------------------------- */
export const listDepartments = (companyId = null) => {
  const path = `/api/departments${companyId ? `?company_id=${companyId}` : ''}`;
  return cachedRequest(path, { ttlMs: 60000 });
};
export const createDepartment = (body) =>
  mutateAndClear(['/api/departments'], () => jsonRequest('/api/departments', 'POST', body)); // { name, company_id }
export const updateDepartment = (id, body) =>
  mutateAndClear(['/api/departments'], () =>
    jsonRequest(`/api/departments/${encodeURIComponent(id)}`, 'PUT', body)
  );
export const deleteDepartment = (id) =>
  mutateAndClear(['/api/departments'], () =>
    request(`/api/departments/${encodeURIComponent(id)}`, { method: 'DELETE' })
  );

/* ------------------------ impressoras --------------------------- */
export const listPrinters = (companyId = null) => {
  const path = `/api/printers${companyId ? `?company_id=${companyId}` : ''}`;
  return cachedRequest(path, { ttlMs: 60000 });
};
export const createPrinter = (body) =>
  mutateAndClear(['/api/printers'], () => jsonRequest('/api/printers', 'POST', body)); // { name, description?, install_cmd, company_id }
export const updatePrinter = (id, body) =>
  mutateAndClear(['/api/printers'], () =>
    jsonRequest(`/api/printers/${encodeURIComponent(id)}`, 'PUT', body)
  );
export const deletePrinter = (id) =>
  mutateAndClear(['/api/printers'], () =>
    request(`/api/printers/${encodeURIComponent(id)}`, { method: 'DELETE' })
  );
export const installPrinterRemote = (id, body) =>
  jsonRequest(`/api/printers/${encodeURIComponent(id)}/install`, 'POST', body); // { machine_uuid, session_id?, auto_finalize? }
export const installPrinterRemoteBulk = (id, body) =>
  jsonRequest(`/api/printers/${encodeURIComponent(id)}/install-bulk`, 'POST', body); // { company_id?, department_id?, machine_uuids?, online_only?, session_id?, auto_finalize? }
export const listPrinterDriverPackages = (companyId = null) => {
  const path = `/api/printer-driver-packages${companyId ? `?company_id=${companyId}` : ''}`;
  return cachedRequest(path, { ttlMs: 60000 });
};
export const createPrinterDriverPackage = (formData) =>
  mutateAndClear(['/api/printer-driver-packages', '/api/printers'], () =>
    request('/api/printer-driver-packages', { method: 'POST', body: formData })
  );
export const updatePrinterDriverPackage = (id, body) =>
  mutateAndClear(['/api/printer-driver-packages', '/api/printers'], () =>
    jsonRequest(`/api/printer-driver-packages/${encodeURIComponent(id)}`, 'PUT', body || {})
  );
export const deletePrinterDriverPackage = (id) =>
  mutateAndClear(['/api/printer-driver-packages', '/api/printers'], () =>
    request(`/api/printer-driver-packages/${encodeURIComponent(id)}`, { method: 'DELETE' })
  );

/* -------------------------- branding ----------------------------- */
export const getBranding = (companyId) =>
  request(`/branding/api/${encodeURIComponent(companyId)}`);

export const listBrandingDeliveries = (companyId) =>
  request(`/branding/api/${encodeURIComponent(companyId)}/deliveries`);

export const dispatchBranding = (companyId, body) =>
  jsonRequest(`/branding/api/${encodeURIComponent(companyId)}/dispatch`, 'POST', body);

export const retryBrandingDelivery = (companyId, machineUuid) =>
  jsonRequest(`/branding/api/${encodeURIComponent(companyId)}/deliveries/${encodeURIComponent(machineUuid)}/retry`, 'POST', {});

export const reapplyBrandingDelivery = (companyId, machineUuid) =>
  jsonRequest(`/branding/api/${encodeURIComponent(companyId)}/deliveries/${encodeURIComponent(machineUuid)}/reapply`, 'POST', {});

export const uploadBranding = (companyId, type, file) => {
  const fd = new FormData();
  fd.append('file', file);
  return mutateAndClear([`/branding/api/${encodeURIComponent(companyId)}`], () =>
    request(
      `/branding/api/${encodeURIComponent(companyId)}/upload?type=${encodeURIComponent(type)}`,
      { method: 'POST', body: fd }
    )
  );
};

export const setCampaign = (companyId, { enabled, start, end }) =>
  mutateAndClear([`/branding/api/${encodeURIComponent(companyId)}`], () =>
    jsonRequest(
      `/branding/api/${encodeURIComponent(companyId)}/campaign`,
      'PUT',
      { enabled, start, end }
    )
  );

/* -------------------------- usuários ----------------------------- */
export const listUsers = () => request('/api/users');
export const createUser = (body) =>
  jsonRequest('/api/users', 'POST', body); // { username, password, role, company_id }
export const updateUser = (id, body) =>
  jsonRequest(`/api/users/${encodeURIComponent(id)}`, 'PUT', body); // { username, role, company_id }
export const updateUserPassword = (id, password) =>
  jsonRequest(`/api/users/${encodeURIComponent(id)}/password`, 'PUT', { password });
export const deleteUser = (id) =>
  request(`/api/users/${encodeURIComponent(id)}`, { method: 'DELETE' });

/* ----------------------- links públicos -------------------------- */
export const createPublicLink = (body) =>
  jsonRequest('/api/public-links', 'POST', body);
// body esperado:
// {
//   "label": "Texto amigável aqui",
//   "company_id": 1,                // opcional
//   "template_ids": [1, 2, 3],      // ids dos CommandTemplate
//   "max_uses": 10                  // opcional, null = ilimitado
// }

export const listPublicLinks = () =>
  request('/api/public-links');

export const deletePublicLink = (id) =>
  request(`/api/public-links/${encodeURIComponent(id)}`, { method: 'DELETE' });

/* ------------------- whatsapp / tickets -------------------------- */
export const listWhatsAppConnections = () =>
  request('/whatsapp/api/connections');
export const createWhatsAppConnection = (body) =>
  jsonRequest('/whatsapp/api/connections', 'POST', body);
export const updateWhatsAppConnection = (id, body) =>
  jsonRequest(`/whatsapp/api/connections/${encodeURIComponent(id)}`, 'PATCH', body);
export const deleteWhatsAppConnection = (id) =>
  request(`/whatsapp/api/connections/${encodeURIComponent(id)}`, { method: 'DELETE' });
export const refreshWhatsAppQrCode = (id, qr_code = null) =>
  jsonRequest(`/whatsapp/api/connections/${encodeURIComponent(id)}/qrcode`, 'POST', { qr_code });
export const setWhatsAppConnectionStatus = (id, body) =>
  jsonRequest(`/whatsapp/api/connections/${encodeURIComponent(id)}/status`, 'POST', body);
export const syncWhatsAppConnectionContacts = (id, params = {}) => {
  const qs = new URLSearchParams();
  if (params.include_non_my_contact != null) {
    qs.set('include_non_my_contact', params.include_non_my_contact ? 'true' : 'false');
  }
  const query = qs.toString();
  return jsonRequest(
    `/whatsapp/api/connections/${encodeURIComponent(id)}/contacts/sync${query ? `?${query}` : ''}`,
    'POST',
    {}
  );
};

export const listTickets = (params = {}) => {
  const qs = new URLSearchParams();
  if (params.status) qs.set('status', params.status);
  if (params.q) qs.set('q', params.q);
  if (params.ticket_id != null) qs.set('ticket_id', String(params.ticket_id));
  if (params.company_id != null) qs.set('company_id', String(params.company_id));
  if (params.connection_id != null) qs.set('connection_id', String(params.connection_id));
  if (params.contact_id != null) qs.set('contact_id', String(params.contact_id));
  if (params.machine_uuid) qs.set('machine_uuid', params.machine_uuid);
  if (params.machine_q) qs.set('machine_q', params.machine_q);
  if (params.assigned_to) qs.set('assigned_to', params.assigned_to);
  if (params.is_group != null) qs.set('is_group', params.is_group ? 'true' : 'false');
  if (params.has_machine != null) qs.set('has_machine', params.has_machine ? 'true' : 'false');
  if (params.has_pending_agenda != null) qs.set('has_pending_agenda', params.has_pending_agenda ? 'true' : 'false');
  if (params.message_q) qs.set('message_q', params.message_q);
  if (params.created_from) qs.set('created_from', params.created_from);
  if (params.created_to) qs.set('created_to', params.created_to);
  if (params.last_message_from) qs.set('last_message_from', params.last_message_from);
  if (params.last_message_to) qs.set('last_message_to', params.last_message_to);
  if (params.limit != null) qs.set('limit', String(params.limit));
  if (params.before_last_message_at) qs.set('before_last_message_at', params.before_last_message_at);
  if (params.before_id != null) qs.set('before_id', String(params.before_id));
  const query = qs.toString();
  return request(`/whatsapp/api/tickets${query ? `?${query}` : ''}`);
};
export const getTicketsDashboard = (params = {}) => {
  const qs = new URLSearchParams();
  if (params.company_id != null && params.company_id !== '') qs.set('company_id', String(params.company_id));
  if (params.date_from) qs.set('date_from', params.date_from);
  if (params.date_to) qs.set('date_to', params.date_to);
  if (params.days != null) qs.set('days', String(params.days));
  if (params.recent_limit != null) qs.set('recent_limit', String(params.recent_limit));
  const query = qs.toString();
  return request(`/whatsapp/api/tickets-dashboard${query ? `?${query}` : ''}`);
};
export const listTicketContacts = (params = {}) => {
  const qs = new URLSearchParams();
  if (params.q) qs.set('q', params.q);
  if (params.company_id != null) qs.set('company_id', String(params.company_id));
  if (params.limit != null) qs.set('limit', String(params.limit));
  if (params.after_scheduled_at) qs.set('after_scheduled_at', params.after_scheduled_at);
  if (params.after_id != null) qs.set('after_id', String(params.after_id));
  const query = qs.toString();
  return request(`/whatsapp/api/contacts${query ? `?${query}` : ''}`);
};
export const updateTicketContact = (contactId, body) =>
  jsonRequest(`/whatsapp/api/contacts/${encodeURIComponent(contactId)}`, 'PATCH', body);
export const updateTicketContactProfile = (ticketId, body) =>
  jsonRequest(`/whatsapp/api/tickets/${encodeURIComponent(ticketId)}/contact-profile`, 'PATCH', body);
export const openTicketFromContact = (contactId) =>
  jsonRequest(`/whatsapp/api/contacts/${encodeURIComponent(contactId)}/open-ticket`, 'POST', {});
export const getTicket = (ticketId) =>
  request(`/whatsapp/api/tickets/${encodeURIComponent(ticketId)}`);
export const listTicketMessages = (ticketId, params = {}) => {
  const qs = new URLSearchParams();
  if (params.before_id != null) qs.set('before_id', String(params.before_id));
  if (params.limit != null) qs.set('limit', String(params.limit));
  const query = qs.toString();
  return request(`/whatsapp/api/tickets/${encodeURIComponent(ticketId)}/messages${query ? `?${query}` : ''}`);
};
export const addIncomingTicketMessage = (body) =>
  jsonRequest('/whatsapp/api/tickets/incoming', 'POST', body);
export const bindTicket = (ticketId, body) =>
  jsonRequest(`/whatsapp/api/tickets/${encodeURIComponent(ticketId)}/bind`, 'PATCH', body);
export const replyTicket = (ticketId, payloadOrText) => {
  const body =
    payloadOrText && typeof payloadOrText === 'object'
      ? payloadOrText
      : { text: String(payloadOrText || '') };
  return jsonRequest(`/whatsapp/api/tickets/${encodeURIComponent(ticketId)}/reply`, 'POST', body);
};
export const sendTicketCommand = (ticketId, body) =>
  jsonRequest(`/whatsapp/api/tickets/${encodeURIComponent(ticketId)}/command`, 'POST', body);
export const closeTicket = (ticketId) =>
  request(`/whatsapp/api/tickets/${encodeURIComponent(ticketId)}/close`, { method: 'POST' });
export const resolveGroupTicketMedia = (ticketId, messageId) =>
  jsonRequest(
    `/whatsapp/api/tickets/${encodeURIComponent(ticketId)}/messages/${encodeURIComponent(messageId)}/resolve-media`,
    'POST',
    {}
  );
export const listTicketParticipants = (ticketId) =>
  request(`/whatsapp/api/tickets/${encodeURIComponent(ticketId)}/participants`);
export const actionTicketMessage = (ticketId, messageId, body) =>
  jsonRequest(
    `/whatsapp/api/tickets/${encodeURIComponent(ticketId)}/messages/${encodeURIComponent(messageId)}/action`,
    'POST',
    body || {}
  );
export const listTicketAgendas = (params = {}) => {
  const qs = new URLSearchParams();
  if (params.status) qs.set('status', params.status);
  if (params.company_id != null && params.company_id !== '') qs.set('company_id', String(params.company_id));
  if (params.q) qs.set('q', params.q);
  if (params.date_from) qs.set('date_from', params.date_from);
  if (params.date_to) qs.set('date_to', params.date_to);
  if (params.limit != null) qs.set('limit', String(params.limit));
  if (params.after_scheduled_at) qs.set('after_scheduled_at', params.after_scheduled_at);
  if (params.after_id != null) qs.set('after_id', String(params.after_id));
  const query = qs.toString();
  return request(`/whatsapp/api/ticket-agendas${query ? `?${query}` : ''}`);
};
export const listAgendasForTicket = (ticketId) =>
  request(`/whatsapp/api/tickets/${encodeURIComponent(ticketId)}/agendas`);
export const createTicketAgenda = (ticketId, body) =>
  jsonRequest(`/whatsapp/api/tickets/${encodeURIComponent(ticketId)}/agendas`, 'POST', body || {});
export const updateTicketAgenda = (agendaId, body) =>
  jsonRequest(`/whatsapp/api/ticket-agendas/${encodeURIComponent(agendaId)}`, 'PATCH', body || {});
export const createContactTicketAgenda = (body) =>
  jsonRequest('/whatsapp/api/ticket-agendas/contact', 'POST', body || {});

/* --------------------- ordem de serviço TI ---------------------- */
export const listItProducts = (params = {}) => {
  const qs = new URLSearchParams();
  if (params.active != null) qs.set('active', params.active ? 'true' : 'false');
  if (params.company_id != null) qs.set('company_id', String(params.company_id));
  const query = qs.toString();
  return request(`/it/api/products${query ? `?${query}` : ''}`);
};
export const createItProduct = (body) =>
  jsonRequest('/it/api/products', 'POST', body);
export const updateItProduct = (id, body) =>
  jsonRequest(`/it/api/products/${encodeURIComponent(id)}`, 'PUT', body);
export const deactivateItProduct = (id) =>
  request(`/it/api/products/${encodeURIComponent(id)}`, { method: 'DELETE' });

export const listItServiceOrders = (params = {}) => {
  const qs = new URLSearchParams();
  if (params.company_id != null) qs.set('company_id', String(params.company_id));
  if (params.status) qs.set('status', params.status);
  if (params.q) qs.set('q', params.q);
  if (params.limit != null) qs.set('limit', String(params.limit));
  if (params.after_scheduled_at) qs.set('after_scheduled_at', params.after_scheduled_at);
  if (params.after_id != null) qs.set('after_id', String(params.after_id));
  const query = qs.toString();
  return request(`/it/api/service-orders${query ? `?${query}` : ''}`);
};
export const getItServiceOrder = (id) =>
  request(`/it/api/service-orders/${encodeURIComponent(id)}`);
export const createItServiceOrder = (body) =>
  jsonRequest('/it/api/service-orders', 'POST', body);
export const updateItServiceOrder = (id, body) =>
  jsonRequest(`/it/api/service-orders/${encodeURIComponent(id)}`, 'PUT', body);
export const updateItServiceOrderStatus = (id, body) =>
  jsonRequest(`/it/api/service-orders/${encodeURIComponent(id)}/status`, 'PATCH', body);
export const getItServiceOrderPdf = (id) =>
  request(`/it/api/service-orders/${encodeURIComponent(id)}/pdf`, {
    responseType: 'blob',
    headers: { Accept: 'application/pdf' },
  });


/* ----------------------- export default -------------------------- */
export default {
  request,
  openStream,
  logIn,
  logOut,
  // máquinas
  listMachines,
  listMachinesPaged,
  getMachineDetail,
  listMachineReplacementCandidates,
  listMachineStatuses,
  getOnlineMachines,
  getMachinesOfflineLog,
  getSessions,
  getMachineUserHistory,
  listMachineBlockedUsers,
  createMachineBlockedUser,
  unblockMachineBlockedUser,
  deleteMachine,
  archiveMachine,
  restoreMachine,
  updateMachine,
  updateMachineDepartmentBulk,
  configureMachineOnlineAlerts,
  getNetworkScanConfig,
  updateNetworkScanConfig,
  listMachineNetworkScans,
  getNetworkScan,
  runMachineNetworkScan,
  importNetworkScanDevices,
  listMachineDiagnostics,
  getMachineDiagnostic,
  runMachineDiagnostic,
  runMachineDiagnosticBulk,
  triggerInventory,
  triggerInventoryBulk,
  triggerRemoteAccessBulk,
  configureMachineOfflineAlertsBulk,
  triggerClientUpdate,
  dropExecutableToMachine,
  executeDroppedExecutableOnMachine,
  uploadClientUpdatePackage,
  triggerClientUpdateBulk,
  listMachineLocations,
  getMachineLocationHistory,
  dispatchMachineLocationCollection,
  listAssets,
  createAsset,
  updateAsset,
  listAssetAssignmentHistory,
  listCollaborators,
  createCollaborator,
  updateCollaborator,
  getPreference,
  setPreference,
  getRustDeskConfig,
  updateRustDeskConfig,
  listRustDeskServerPeers,
  listAgentTools,
  listAgentCommandCatalog,
  runAgentAction,
  listAgentApprovals,
  createAgentApproval,
  approveAgentApproval,
  rejectAgentApproval,
  listAgentActionLogs,
  // templates
  getTemplates,
  addTemplate,
  updateTemplate,
  deleteTemplate,
  // comandos
  createCommand,
  createBulk,
  listBulkLogs,
  listCommandHistory,
  listServerAlertHistory,
  cleanupCommands,
  cancelCommand,
  runDomainAction,
  getCommandStatus,
  listTicketCommands,
  // scripts
  listScriptPacks,
  uploadScriptPack,
  deleteScriptPack,
  sendScriptPack,
  sendScriptPackBulk,
  // aplicativos
  listApplicationPackages,
  createApplicationPackage,
  createApplicationPackageVersion,
  updateApplicationPackage,
  deactivateApplicationPackage,
  installApplicationPackage,
  listApplicationDeployments,
  // companhias
  listCompanies,
  createCompany,
  updateCompany,
  deleteCompany,
  // vpn
  listVpnProfiles,
  createVpnProfile,
  updateVpnProfile,
  deleteVpnProfile,
  listVpnPeers,
  deployVpnProfile,
  listVpnDirectCandidates,
  listVpnDirectLinks,
  refreshVpnDirectRuntime,
  createVpnDirectLink,
  deleteVpnDirectLink,
  updateMachineVpnDirectAccess,
  getVpnGatewayStatus,
  listVpnGatewayFlows,
  syncVpnGateway,
  // departamentos
  listDepartments,
  createDepartment,
  updateDepartment,
  deleteDepartment,
  // impressoras
  listPrinters,
  createPrinter,
  updatePrinter,
  deletePrinter,
  installPrinterRemote,
  installPrinterRemoteBulk,
  listPrinterDriverPackages,
  createPrinterDriverPackage,
  updatePrinterDriverPackage,
  deletePrinterDriverPackage,
  // branding
  getBranding,
  listBrandingDeliveries,
  dispatchBranding,
  retryBrandingDelivery,
  reapplyBrandingDelivery,
  uploadBranding,
  setCampaign,
  // ✅ usuários
  listUsers,
  createUser,
  updateUser,
  updateUserPassword,
  deleteUser,
  // links públicos
  createPublicLink,
  listPublicLinks,
  deletePublicLink,
  // whatsapp / tickets
  listWhatsAppConnections,
  createWhatsAppConnection,
  updateWhatsAppConnection,
  deleteWhatsAppConnection,
  refreshWhatsAppQrCode,
  setWhatsAppConnectionStatus,
  syncWhatsAppConnectionContacts,
  getTicketsDashboard,
  listTickets,
  listTicketContacts,
  updateTicketContact,
  updateTicketContactProfile,
  openTicketFromContact,
  getTicket,
  listTicketMessages,
  addIncomingTicketMessage,
  bindTicket,
  replyTicket,
  sendTicketCommand,
  closeTicket,
  resolveGroupTicketMedia,
  listTicketParticipants,
  actionTicketMessage,
  listTicketAgendas,
  listAgendasForTicket,
  createTicketAgenda,
  createContactTicketAgenda,
  updateTicketAgenda,
  // ordem de serviço TI
  listItProducts,
  createItProduct,
  updateItProduct,
  deactivateItProduct,
  listItServiceOrders,
  getItServiceOrder,
  createItServiceOrder,
  updateItServiceOrder,
  updateItServiceOrderStatus,
  getItServiceOrderPdf,
};
